/**
 * Fake Leaderboard Generator
 * Generates a consistent set of fake players for leaderboards and matchmaking
 * Uses seeded random for consistent results across renders
 */

import { generateAvatarFromUsername, type AvatarConfig } from './avatar-config';

export interface FakePlayer {
  id: string;
  username: string;
  display_name: string;
  elo_rating: number;
  games_played: number;
  games_won: number;
  games_lost: number;
  games_drawn: number;
  isFake: true;
  isPrivate: boolean;
  avatarConfig: AvatarConfig;
}

// Seeded random for consistent generation
function seededRandom(seed: number): number {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// International first names - diverse cultures
const internationalNames = [
  // English
  "Alex", "Jordan", "Taylor", "Casey", "Morgan", "Riley", "Max", "Sam", "Chris", "Jamie",
  // European
  "Luca", "Marco", "Erik", "Henrik", "Lars", "Sven", "Pierre", "Jean", "Hans", "Felix",
  "Anton", "Klaus", "Matteo", "Giovanni", "Paolo", "Franz", "Otto", "Emil", "Oscar", "Hugo",
  // Nordic
  "Bjorn", "Sigurd", "Ragnar", "Ingrid", "Astrid", "Freya", "Thor", "Odin", "Magnus", "Leif",
  // Asian
  "Wei", "Yuki", "Kenji", "Min", "Jin", "Hiro", "Ryu", "Koji", "Akira", "Takeshi",
  "Chen", "Liu", "Park", "Kim", "Lee", "Sato", "Tanaka", "Yamamoto", "Nakamura", "Suzuki",
  // Latin American
  "Carlos", "Diego", "Miguel", "Pablo", "Luis", "Raul", "Jose", "Pedro", "Fernando", "Ricardo",
  "Sofia", "Isabella", "Valentina", "Camila", "Luna", "Emilia", "Victoria", "Elena", "Clara",
  // Indian
  "Arjun", "Vikram", "Raj", "Priya", "Aisha", "Neha", "Aahana", "Dev", "Rahul", "Amit",
  "Kiran", "Sanjay", "Ravi", "Deepak", "Ananya", "Ishaan", "Rohan", "Aditya", "Kavya",
  // Russian/Slavic
  "Nikita", "Dmitri", "Ivan", "Sergei", "Yuri", "Vlad", "Boris", "Alexei", "Mikhail", "Andrei",
  "Natasha", "Olga", "Katya", "Anya", "Viktor", "Pavel", "Oleg", "Igor", "Maxim",
  // Middle Eastern
  "Omar", "Karim", "Hassan", "Zara", "Layla", "Amir", "Tariq", "Samir", "Farid", "Yasmin",
  "Nadia", "Fatima", "Leila", "Rania", "Khalid", "Jamal", "Rashid", "Waleed", "Hamza",
  // African
  "Kofi", "Kwame", "Zuri", "Amara", "Ade", "Femi", "Chidi", "Nia", "Imani", "Jabari",
  "Tendai", "Sekou", "Ola", "Yemi", "Tunde", "Ngozi", "Chioma", "Adaeze", "Emeka"
];

// Chess-related words
const chessWords = [
  "Knight", "Rook", "Bishop", "Pawn", "Castle", "Gambit", "Check", "Mate", "King", "Queen",
  "Chess", "Master", "Grand", "Blitz", "Rapid", "Bullet", "Endgame", "Opening", "Tactical",
  "Sacrifice", "Fork", "Pin", "Skewer", "Tempo", "Zugzwang", "Stalemate", "Checkmate"
];

// Action words
const actionWords = [
  "Strikes", "Attacks", "Defends", "Crushes", "Wins", "Plays", "Thinks", "Moves",
  "Captures", "Controls", "Dominates", "Conquers", "Outplays", "Hunts", "Stalks"
];

// Adjectives
const adjectives = [
  "Quick", "Fast", "Smart", "Clever", "Silent", "Bold", "Fierce", "Calm", "Cold", "Wild",
  "Dark", "Light", "Bright", "Sharp", "Swift", "Sly", "Wise", "Mad", "Cool", "Epic",
  "Royal", "Noble", "Deadly", "Savage", "Mystic", "Phantom", "Ghost", "Shadow", "Storm"
];

// Animals
const animals = [
  "Wolf", "Bear", "Eagle", "Fox", "Lion", "Shark", "Panther", "Raven", "Tiger", "Hawk",
  "Viper", "Cobra", "Dragon", "Phoenix", "Falcon", "Leopard", "Jaguar", "Owl", "Crow",
  "Spider", "Scorpion", "Mantis", "Hydra", "Griffin", "Kraken", "Serpent"
];

// Colors
const colors = ["Red", "Blue", "Dark", "Gold", "Silver", "Black", "White", "Grey", "Crimson", "Azure", "Jade", "Onyx"];

// Prefixes and suffixes
const prefixes = ["xX", "Pro", "The", "Mr", "Dr", "Sir", "Lord", "El", "La", "Der", "Le", ""];
const suffixes = ["_YT", "_TV", "_GG", "_Pro", "HD", "", "", "", "", ""];

// Numbers for usernames
const numberSuffixes = ["", "99", "23", "12", "007", "42", "88", "77", "13", "21", "2024", "2025", "1337", "69", "100", "360", "404", "911", "666", "777", ""];

function pickRandom<T>(arr: T[], seed: number): T {
  return arr[Math.floor(seededRandom(seed) * arr.length)];
}

// Generate username with many varied patterns
function generateSeededUsername(seed: number): string {
  const pattern = Math.floor(seededRandom(seed) * 15);
  const name = pickRandom(internationalNames, seed + 1);
  const chessWord = pickRandom(chessWords, seed + 2);
  const action = pickRandom(actionWords, seed + 3);
  const adj = pickRandom(adjectives, seed + 4);
  const animal = pickRandom(animals, seed + 5);
  const color = pickRandom(colors, seed + 6);
  const prefix = pickRandom(prefixes, seed + 7);
  const suffix = pickRandom(suffixes, seed + 8);
  const num = pickRandom(numberSuffixes, seed + 9);
  
  let username: string;
  switch (pattern) {
    case 0: username = `${name}${num}`; break;                          // Alex2024
    case 1: username = `${chessWord}${animal}${num}`; break;            // ChessWolf99
    case 2: username = `${adj}${chessWord}${num}`; break;               // QuickMate77
    case 3: username = `${name}_${chessWord}`; break;                   // Carlos_Gambit
    case 4: username = `${color}${animal}${num}`; break;                // DarkWolf99
    case 5: username = `${name}${adj}${chessWord}`; break;              // MaxQuickCheck
    case 6: username = `${chessWord}${chessWord}${num}`; break;         // ChessMaster42
    case 7: username = `${adj}${name}`; break;                          // SilentViktor
    case 8: username = `${prefix}${name}${animal}${suffix}`; break;     // xXAlexWolf_YT
    case 9: username = `${animal}${action}${num}`; break;               // WolfStrikes23
    case 10: username = `${color}${adj}${num}`; break;                  // DarkSilent88
    case 11: username = `${name}${name.slice(0,2)}${num}`; break;       // AlexAl42
    case 12: username = `${adj}${animal}${chessWord}`; break;           // SwiftEagleKnight
    case 13: username = `${chessWord}${name}${num}`; break;             // GambitCarlos77
    case 14: username = `${prefix}${adj}${animal}`; break;              // TheSwiftFox
    default: username = `${name}${chessWord}${num}`; break;
  }
  
  return username.replace(/_{2,}/g, '_').replace(/^_|_$/g, '') || `Player${Math.floor(seededRandom(seed + 100) * 9999)}`;
}

// Generate realistic ELO following a bell curve
function generateRealisticElo(seed: number): number {
  // Use Box-Muller for normal distribution
  const u1 = seededRandom(seed);
  const u2 = seededRandom(seed + 0.5);
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  
  // Mean 1200, std dev 300
  const elo = Math.round(1200 + z * 300);
  return Math.max(400, Math.min(2800, elo));
}

function eloToWinRate(elo: number): number {
  // Linear interpolation: 400 ELO = 30% win rate, 2800 ELO = 75% win rate
  const minWinRate = 0.30;
  const maxWinRate = 0.75;
  const normalizedElo = (elo - 400) / (2800 - 400);
  return minWinRate + normalizedElo * (maxWinRate - minWinRate);
}

// Cache the generated leaderboard so it's consistent
let cachedLeaderboard: FakePlayer[] | null = null;

/**
 * Generate 973 fake players with consistent data
 */
export function generateFakeLeaderboard(): FakePlayer[] {
  if (cachedLeaderboard) return cachedLeaderboard;
  
  const players: FakePlayer[] = [];
  const usedUsernames = new Set<string>();
  
  for (let i = 0; i < 973; i++) {
    const seed = i * 7919; // Prime number for better distribution
    
    // Generate unique username
    let username = generateSeededUsername(seed);
    let attempts = 0;
    while (usedUsernames.has(username.toLowerCase()) && attempts < 10) {
      username = generateSeededUsername(seed + attempts * 1000);
      attempts++;
    }
    usedUsernames.add(username.toLowerCase());
    
    const elo = generateRealisticElo(seed + 1);
    const winRate = eloToWinRate(elo);
    const gamesPlayed = Math.floor(seededRandom(seed + 2) * 500) + 20;
    const gamesWon = Math.floor(gamesPlayed * winRate);
    const gamesDrawn = Math.floor(gamesPlayed * 0.15);
    const gamesLost = gamesPlayed - gamesWon - gamesDrawn;
    
    // Only ~6% of profiles are private
    const isPrivate = seededRandom(seed + 500) < 0.06;
    
    // Generate avatar config based on username
    const avatarConfig = generateAvatarFromUsername(username);
    
    players.push({
      id: `fake-player-${i}`,
      username,
      display_name: username,
      elo_rating: elo,
      games_played: gamesPlayed,
      games_won: gamesWon,
      games_lost: Math.max(0, gamesLost),
      games_drawn: gamesDrawn,
      isFake: true,
      isPrivate,
      avatarConfig
    });
  }
  
  // Sort by ELO descending
  players.sort((a, b) => b.elo_rating - a.elo_rating);
  cachedLeaderboard = players;
  return cachedLeaderboard;
}

/**
 * Get fake players within a specific ELO range
 * Used for matchmaking to find opponents near player's rating
 */
export function getFakePlayersByEloRange(minElo: number, maxElo: number): FakePlayer[] {
  const leaderboard = generateFakeLeaderboard();
  return leaderboard.filter(p => p.elo_rating >= minElo && p.elo_rating <= maxElo);
}

/**
 * Get a random fake player near a specific ELO
 * Returns both username and elo for matchmaking
 */
export function getRandomFakeOpponent(targetElo: number, tolerance: number = 150): { username: string; elo: number } {
  const candidates = getFakePlayersByEloRange(targetElo - tolerance, targetElo + tolerance);
  
  if (candidates.length === 0) {
    // Fallback if no candidates in range
    const allPlayers = generateFakeLeaderboard();
    const sorted = allPlayers
      .map(p => ({ ...p, diff: Math.abs(p.elo_rating - targetElo) }))
      .sort((a, b) => a.diff - b.diff);
    
    const selected = sorted[Math.floor(Math.random() * Math.min(10, sorted.length))];
    return { username: selected.username, elo: selected.elo_rating };
  }
  
  const selected = candidates[Math.floor(Math.random() * candidates.length)];
  return { username: selected.username, elo: selected.elo_rating };
}

/**
 * Get total player count (real + fake)
 */
export function getTotalPlayerCount(realPlayerCount: number): number {
  return realPlayerCount + 973;
}

// Search fake players by username query and ELO filters
export function searchFakePlayers(
  query: string,
  minElo: number = 0,
  maxElo: number = 3000
): FakePlayer[] {
  if (!query.trim()) return [];
  
  const leaderboard = generateFakeLeaderboard();
  const lowerQuery = query.toLowerCase();
  
  return leaderboard.filter(p => 
    p.username.toLowerCase().includes(lowerQuery) &&
    p.elo_rating >= minElo &&
    p.elo_rating <= maxElo
  ).slice(0, 50);
}
